import React from 'react';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram } from 'lucide-react';
import { footer, contact, socialLinks, images } from '../../content';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Valores por defecto
const defaultContact = {
  phone: "(656) 536-7364",
  email: "credito@confiare.mx",
  address: "Ciudad Juárez, Chihuahua",
  hours: "Lunes a Viernes: 9:00 AM - 6:00 PM"
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  // Obtener datos del contenido con fallbacks
  const contactInfo = {
    phone: contact?.phone || defaultContact.phone,
    email: contact?.email || defaultContact.email,
    address: contact?.address || defaultContact.address,
    hours: contact?.hours || defaultContact.hours
  };

  const social = {
    facebook: socialLinks?.facebook || "#",
    instagram: socialLinks?.instagram || "#",
    tiktok: socialLinks?.tiktok || "#"
  };

  const footerContent = {
    description: footer?.description || "Tu aliado financiero de confianza.",
    quickLinks: footer?.quickLinks?.items || [],
    quickLinksTitle: footer?.quickLinks?.title || "Enlaces rápidos",
    productsTitle: footer?.productsLinks?.title || "Productos",
    products: footer?.productsLinks?.items || [],
    copyright: footer?.copyright || "CONFIARÉ. Todos los derechos reservados.",
    privacyText: footer?.privacyLink?.text || "Aviso de Privacidad",
    privacyHref: footer?.privacyLink?.href || "/aviso-de-privacidad"
  };

  const logoUrl = images?.logo || "/images/logo-confiare.png";

  const socialLinksArray = [
    { Icon: Facebook, href: social.facebook },
    { Icon: Instagram, href: social.instagram },
    { Icon: TikTokIcon, href: social.tiktok }
  ];

  return (
    <footer className="bg-[#013C5D] text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="space-y-6">
            <div className="flex items-center gap-2">
              <img 
                src={logoUrl}
                alt="CONFIARÉ Logo" 
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-white/70 text-sm leading-relaxed">
              {footerContent.description}
            </p>
            <div className="flex gap-3">
              {socialLinksArray.map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#CFA935] hover:text-[#013C5D] transition-all duration-300"
                >
                  <social.Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{footerContent.quickLinksTitle}</h4>
            <ul className="space-y-3">
              {footerContent.quickLinks.map((link, idx) => (
                <li key={idx}>
                  <a
                    href={link.href || "#"}
                    className="text-white/70 hover:text-[#CFA935] transition-colors text-sm"
                  >
                    {link.text || link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg font-semibold mb-6">{footerContent.productsTitle}</h4>
            <ul className="space-y-3">
              {footerContent.products.map((product, idx) => (
                <li key={idx}>
                  <a
                    href="#creditos"
                    className="text-white/70 hover:text-[#CFA935] transition-colors text-sm"
                  >
                    {product}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6">Contacto</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#CFA935] flex-shrink-0 mt-0.5" />
                <span className="text-white/70 text-sm">{contactInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#CFA935] flex-shrink-0" />
                <span className="text-white/70 text-sm">{contactInfo.phone}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#CFA935] flex-shrink-0" />
                <span className="text-white/70 text-sm">{contactInfo.email}</span>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-[#CFA935] flex-shrink-0" />
                <span className="text-white/70 text-sm">{contactInfo.hours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/60 text-sm">
              © {currentYear} {footerContent.copyright}
            </p>
            <div className="flex gap-6">
              <a href={footerContent.privacyHref} className="text-white/60 hover:text-[#CFA935] text-sm transition-colors">
                {footerContent.privacyText}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
